// The send wizard's rules (docs/adr/0014): what can travel which way.
// The choices live in the URL (/send?what=text&how=drop), so the browser's
// Back button walks back through the steps and a step can be linked to.

export const WHATS = ['text', 'small', 'large'] as const;
export const HOWS = ['drop', 'live', 'relay'] as const;

export type What = (typeof WHATS)[number];
/** `lock` is the quieter fourth way: a password-locked file the sender sends themselves. */
export type How = (typeof HOWS)[number] | 'lock';
export type Step = 1 | 2 | 3;

export interface Choice {
  what: What | null;
  how: How | null;
  step: Step;
}

const isWhat = (value: string | null): value is What => (WHATS as readonly (string | null)[]).includes(value);
const isHow = (value: string | null): value is How => value === 'lock' || (HOWS as readonly (string | null)[]).includes(value);

/**
 * Whether `how` is offered for `what`. Drop takes text always, and files
 * where stored drops are switched on (`dropsForFiles`, from the Worker's
 * GET /v1/drops; docs/adr/0015). A password-locked file is capped at the
 * local tools' 32 MB, so large files can't take it.
 */
export function isAvailable(what: What, how: How, dropsForFiles = false): boolean {
  if (how === 'drop') return what === 'text' || dropsForFiles;
  if (how === 'lock') return what !== 'large';
  return true;
}

/** The way the wizard marks as the best fit. */
export const recommended = (what: What): How => (what === 'text' ? 'drop' : 'live');

/** Read the choice from the URL. Anything unknown or unavailable sends the visitor back a step. */
export function parseChoice(params: URLSearchParams, dropsForFiles = false): Choice {
  const what = params.get('what');
  if (!isWhat(what)) return { what: null, how: null, step: 1 };
  const how = params.get('how');
  if (!isHow(how) || !isAvailable(what, how, dropsForFiles)) return { what, how: null, step: 2 };
  return { what, how, step: 3 };
}

/** The query string for a choice. */
export function choiceSearch(what: What | null, how: How | null = null): string {
  if (!what) return '';
  return how ? `?what=${what}&how=${how}` : `?what=${what}`;
}
